/**
 * User Activity Service
 * Fetches CloudWatch Logs Insights analytics from the admin Lambda.
 *   GET /admin/user-activity?hours=168 -> { widgets, metadata }
 *
 * Note: widget `type` reflects the raw data shape from CloudWatch —
 * the FE may render a widget with a different chart type (see
 * UserActivityDashboard/index.tsx for the actual mapping).
 */
import { AuthService } from './auth.service';
import { apiConfig } from '@/config/api.config';
import { API_ENVIRONMENTS } from '@/config/constant';

export interface BarChartWidget {
  type: 'barChart';
  title: string;
  labels: string[];
  values: number[];
  totalCalls: number;
}

export interface LineChartWidget {
  type: 'lineChart';
  title: string;
  labels: string[];
  values: number[];
  avgDailyUsers?: number;
  latestWAU?: number;
}

export interface HorizontalBarChartWidget {
  type: 'horizontalBarChart';
  title: string;
  labels: string[];
  values: number[];
  totalUniqueUsers: number;
}

export interface TableWidget {
  type: 'table';
  title: string;
  columns: string[];
  rows: Array<Record<string, string | number>>;
}

export interface NumberCardsWidget {
  type: 'numberCards';
  title: string;
  cards: {
    totalApiCalls: number;
    avgDailyUsers: number;
    totalUniqueUsers: number;
    latestWeeklyActiveUsers: number;
    daysTracked: number;
  };
}

export interface UserActivityWidgets {
  totalApiCallsPerDay: BarChartWidget;
  dailyActiveUsers: LineChartWidget;
  topUsers: HorizontalBarChartWidget;
  apiCallsPerUserPerDay: TableWidget;
  weeklyActiveUsersTrend: LineChartWidget;
  summary: NumberCardsWidget;
}

export interface UserActivityMetadata {
  timeRange: string;
  startTime: string;
  endTime: string;
  logGroups: string[];
  queriedBy: string;
  queryTime: string;
  maxPollSeconds: number;
  partialResults: boolean;
}

export interface UserActivityResponse {
  widgets: UserActivityWidgets;
  metadata: UserActivityMetadata;
}

class UserActivityService {
  /** dev: proxied via /api; prod: full admin API Gateway URL */
  private getBaseUrl(): string {
    if (process.env.NODE_ENV === API_ENVIRONMENTS.DEV) {
      return '/api';
    }
    return apiConfig.adminBaseUrl.replace(/\/$/, '');
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const idToken = await AuthService.getIdToken();
    if (!idToken) {
      throw new Error('No authentication token available');
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    };
  }

  private async parseError(response: Response): Promise<string> {
    const text = await response.text().catch(() => '');
    try {
      const json = JSON.parse(text) as { error?: string; message?: string };
      return json.error || json.message || `HTTP ${response.status}: ${response.statusText}`;
    } catch {
      return `HTTP ${response.status}: ${response.statusText}`;
    }
  }

  /** @param hours Lookback window in hours (default 168 = 1 week) */
  async getUserActivity(hours = 168): Promise<UserActivityResponse> {
    const headers = await this.getAuthHeaders();
    const url = `${this.getBaseUrl()}${apiConfig.endpoints.adminUserActivity}?hours=${hours}`;

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    return response.json() as Promise<UserActivityResponse>;
  }
}

export const userActivityService = new UserActivityService();
export default userActivityService;
